import type { IPickPageMode, IPickPageModeConfig, IPickPageTypeConfig } from '../types';

import { TEMPLATE_IMAGE } from '~/constants/template';
import { IPickPageType } from '../types';

export const PICK_PAGE_MODE = {
  /** Price test — the experiment runs on a product page, so only products can be picked. */
  PRICE: 'PRICE',
  /** URL test — any storefront page qualifies, so picks are grouped by page type. */
  URL: 'URL',
} as const;

export const PICK_PAGE_MODE_CONFIG: Record<IPickPageMode, IPickPageModeConfig> = {
  [PICK_PAGE_MODE.PRICE]: {
    title: 'Choose product to test',
    confirmContent: 'Select product',
    searchPlaceholder: 'Search products',
    types: [IPickPageType.PRODUCT],
    hasSort: true,
    hasStatusBadge: true,
  },
  [PICK_PAGE_MODE.URL]: {
    // `{{target}}` reads as the variant the modal was opened for — "Control" or "Variant".
    title: 'Select a URL for {{target}}',
    confirmContent: 'Select URL',
    searchPlaceholder: 'Search or enter a URL',
    types: [
      IPickPageType.HOME,
      IPickPageType.LANDING,
      IPickPageType.PRODUCT,
      IPickPageType.COLLECTION,
      IPickPageType.BLOG_POST,
    ],
    hasSort: false,
    hasStatusBadge: false,
    // A merged search offers no single page type to create, so it carries no `createAdminPath`.
    searchAllConfig: {
      label: 'All',
      resourceName: { singular: 'page', plural: 'pages' },
      emptyHeading: 'No page found',
      emptySourceDescription: "Your store haven't had any page yet.",
      emptyImage: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
    },
  },
};

export const PICK_PAGE_TYPE_CONFIG: Record<IPickPageType, IPickPageTypeConfig> = {
  [IPickPageType.HOME]: {
    label: 'Home',
    resourceName: { singular: 'page', plural: 'pages' },
    emptyHeading: 'No homepage found',
    emptySourceDescription: 'Your store has no homepage yet.',
    emptyImage: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
  },
  [IPickPageType.LANDING]: {
    label: 'Landing',
    resourceName: { singular: 'page', plural: 'pages' },
    emptyHeading: 'No page found',
    emptySourceDescription: "Your store haven't had any page yet.",
    emptyImage: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
    createAdminPath: 'pages/new',
  },
  [IPickPageType.PRODUCT]: {
    label: 'Products',
    resourceName: { singular: 'product', plural: 'products' },
    emptyHeading: 'No product found',
    emptySourceDescription: "Your store haven't had any product yet.",
    emptyImage: TEMPLATE_IMAGE.EMPTY_PRODUCT,
    createAdminPath: 'products/new',
  },
  [IPickPageType.COLLECTION]: {
    label: 'Collections',
    resourceName: { singular: 'collection', plural: 'collections' },
    emptyHeading: 'No collection found',
    emptySourceDescription: "Your store haven't had any collection yet.",
    emptyImage: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
    createAdminPath: 'collections/new',
  },
  [IPickPageType.BLOG_POST]: {
    label: 'Blog posts',
    resourceName: { singular: 'blog post', plural: 'blog posts' },
    emptyHeading: 'No blog post found',
    emptySourceDescription: "Your store haven't had any blog post yet.",
    emptyImage: TEMPLATE_IMAGE.EMPTY_TEMPLATE,
    // Blog posts sit under Content in the Shopify admin, unlike the other three.
    createAdminPath: 'content/articles/new',
  },
};
